import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Font,
} from "@react-pdf/renderer";
import logo from "../../assets/ooklass-white-removebg.png";
import QR from "../../assets/ooklass-QR.png";
import { formatDate } from "../../utils";
import NotoSansRegular from "../../assets/NotoSans-Regular.ttf";
import NotoSansBold from "../../assets/NotoSans-Bold.ttf";

Font.register({
  family: "NotoSans",
  fonts: [
    { src: NotoSansRegular, fontWeight: "normal" },
    { src: NotoSansBold, fontWeight: "bold" },
  ],
});

const NAVY = "#010f2e";
const ORANGE = "#fe8366";
const LIGHT_ORANGE = "#fff4f2";
const GREY = "#6b7280";
const LIGHT_GREY = "#f3f4f6";
const BORDER = "#e5e7eb";

const styles = StyleSheet.create({
  page: {
    fontFamily: "NotoSans",
    backgroundColor: "#ffffff",
    fontSize: 10,
    color: NAVY,
  },

  // ── Top banner ──────────────────────────────────────────────────────────────
  banner: {
    backgroundColor: NAVY,
    paddingHorizontal: 36,
    paddingVertical: 20,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  logo: {
    width: 130,
    height: 55,
  },
  bannerRight: {
    alignItems: "flex-end",
  },
  receiptLabel: {
    fontSize: 20,
    fontFamily: "NotoSans", fontWeight: "bold",
    color: "#ffffff",
    letterSpacing: 3,
  },
  receiptSub: {
    fontSize: 9,
    color: ORANGE,
    marginTop: 3,
    letterSpacing: 1,
  },

  // ── Orange accent line ───────────────────────────────────────────────────────
  accentLine: {
    backgroundColor: ORANGE,
    height: 3,
  },

  // ── Body ────────────────────────────────────────────────────────────────────
  body: {
    paddingHorizontal: 36,
    paddingTop: 24,
    paddingBottom: 24,
  },

  // ── Date + address row ───────────────────────────────────────────────────────
  metaRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  addressBlock: {
    width: "55%",
  },
  addressName: {
    fontSize: 11,
    fontFamily: "NotoSans", fontWeight: "bold",
    color: NAVY,
    marginBottom: 2,
  },
  addressLine: {
    fontSize: 9,
    color: GREY,
    lineHeight: 1.5,
  },
  dateBlock: {
    alignItems: "flex-end",
  },
  dateLabel: {
    fontSize: 8,
    color: GREY,
    textTransform: "uppercase",
    letterSpacing: 1,
    marginBottom: 2,
  },
  dateValue: {
    fontSize: 12,
    fontFamily: "NotoSans", fontWeight: "bold",
    color: NAVY,
  },

  // ── Divider ─────────────────────────────────────────────────────────────────
  divider: {
    borderBottom: `1px solid ${BORDER}`,
    marginBottom: 20,
  },

  // ── Bill To card ─────────────────────────────────────────────────────────────
  billToCard: {
    backgroundColor: LIGHT_GREY,
    borderRadius: 6,
    padding: 14,
    marginBottom: 20,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  billToLeft: {
    width: "60%",
  },
  billToRight: {
    width: "35%",
    alignItems: "flex-end",
  },
  billToLabel: {
    fontSize: 8,
    color: ORANGE,
    fontFamily: "NotoSans", fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: 1,
    marginBottom: 6,
  },
  billToName: {
    fontSize: 13,
    fontFamily: "NotoSans", fontWeight: "bold",
    color: NAVY,
    marginBottom: 3,
  },
  billToLine: {
    fontSize: 9,
    color: GREY,
    marginBottom: 2,
  },

  // ── Payment table ────────────────────────────────────────────────────────────
  tableHeader: {
    flexDirection: "row",
    backgroundColor: NAVY,
    borderRadius: 4,
    paddingVertical: 8,
    paddingHorizontal: 12,
    marginBottom: 2,
  },
  tableHeaderCell: {
    color: "#ffffff",
    fontSize: 8.5,
    fontFamily: "NotoSans", fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    flex: 1,
    textAlign: "center",
  },
  tableRow: {
    flexDirection: "row",
    backgroundColor: LIGHT_ORANGE,
    borderRadius: 4,
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginBottom: 8,
  },
  tableCell: {
    flex: 1,
    textAlign: "center",
    fontSize: 10,
    fontFamily: "NotoSans", fontWeight: "bold",
    color: NAVY,
  },

  // ── Fees summary row ─────────────────────────────────────────────────────────
  feesSummaryRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 16,
    marginBottom: 14,
    paddingHorizontal: 4,
  },
  feesSummaryItem: {
    alignItems: "flex-end",
  },
  feesSummaryLabel: {
    fontSize: 8,
    color: GREY,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 1,
  },
  feesSummaryValue: {
    fontSize: 11,
    fontFamily: "NotoSans", fontWeight: "bold",
    color: NAVY,
  },
  feesSummaryValuePending: {
    fontSize: 11,
    fontFamily: "NotoSans", fontWeight: "bold",
    color: "#c0392b",
  },

  // ── Thank you banner ─────────────────────────────────────────────────────────
  thankYou: {
    backgroundColor: LIGHT_ORANGE,
    borderLeft: `3px solid ${ORANGE}`,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 4,
    marginBottom: 20,
  },
  thankYouText: {
    fontSize: 10,
    fontFamily: "NotoSans", fontWeight: "bold",
    color: ORANGE,
  },
  thankYouSub: {
    fontSize: 8.5,
    color: GREY,
    marginTop: 2,
  },

  // ── Footer ───────────────────────────────────────────────────────────────────
  footer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginTop: 8,
  },
  coachingInfo: {
    width: "65%",
  },
  coachingLabel: {
    fontSize: 8,
    color: GREY,
    textTransform: "uppercase",
    letterSpacing: 1,
    marginBottom: 5,
  },
  coachingLine: {
    fontSize: 9,
    color: NAVY,
    marginBottom: 3,
    lineHeight: 1.4,
  },
  qr: {
    width: 80,
    height: 80,
  },

  // ── Signature ────────────────────────────────────────────────────────────────
  signatureSection: {
    marginTop: 24,
    borderTop: `1px solid ${BORDER}`,
    paddingTop: 12,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
  },
  signatureNote: {
    fontSize: 8,
    color: GREY,
  },
  signatureBox: {
    width: 160,
    borderBottom: `1px solid ${NAVY}`,
    paddingBottom: 4,
    alignItems: "center",
  },
  signatureLabel: {
    fontSize: 8,
    color: GREY,
    marginTop: 4,
  },
});

const InvoicePDF = ({ studentData, feesData, allFees = [] }) => {
  const totalFees = feesData.total || 0;
  const totalPaid = allFees.reduce((sum, f) => sum + f.amoutPaid, 0);
  const pending = totalFees - totalPaid;
  const fmt = (n) => `₹ ${Number(n).toLocaleString("en-IN")}`;

  return (
  <Document>
    <Page size="A4" style={styles.page}>

      {/* ── Top banner ── */}
      <View style={styles.banner}>
        <Image src={logo} style={styles.logo} />
        <View style={styles.bannerRight}>
          <Text style={styles.receiptLabel}>FEE RECEIPT</Text>
          <Text style={styles.receiptSub}>OOKLASS TUITION CLASSES</Text>
        </View>
      </View>
      <View style={styles.accentLine} />

      <View style={styles.body}>

        {/* ── Date + institute address ── */}
        <View style={styles.metaRow}>
          <View style={styles.addressBlock}>
            <Text style={styles.addressName}>OOKlass Tuition Classes</Text>
            <Text style={styles.addressLine}>Ward No.27, Near Water Works</Text>
            <Text style={styles.addressLine}>Lalgarh Jattan, SGNR – 335037</Text>
            <Text style={styles.addressLine}>+91-6377132574  ·  +91-7665873094</Text>
          </View>
          <View style={styles.dateBlock}>
            <Text style={styles.dateLabel}>Date of Invoice</Text>
            <Text style={styles.dateValue}>{formatDate(new Date())}</Text>
          </View>
        </View>

        <View style={styles.divider} />

        {/* ── Bill To card ── */}
        <View style={styles.billToCard}>
          <View style={styles.billToLeft}>
            <Text style={styles.billToLabel}>Bill To</Text>
            <Text style={styles.billToName}>{studentData.name}</Text>
            <Text style={styles.billToLine}>Father: {studentData.fathername}</Text>
            <Text style={styles.billToLine}>Contact: {studentData.contact}</Text>
          </View>
          <View style={styles.billToRight}>
            <Text style={styles.billToLabel}>Class</Text>
            <Text style={styles.billToName}>{studentData.class}</Text>
          </View>
        </View>

        {/* ── Payment table ── */}
        <View style={styles.tableHeader}>
          <Text style={styles.tableHeaderCell}>Payment Date</Text>
          <Text style={styles.tableHeaderCell}>Amount Paid</Text>
          <Text style={styles.tableHeaderCell}>Next Due Date</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>{formatDate(new Date(feesData.date))}</Text>
          <Text style={styles.tableCell}>{fmt(feesData.amoutPaid)}</Text>
          <Text style={styles.tableCell}>{formatDate(new Date(feesData.nextDue))}</Text>
        </View>

        {/* ── Fees summary ── */}
        {totalFees > 0 && (
          <View style={styles.feesSummaryRow}>
            <View style={styles.feesSummaryItem}>
              <Text style={styles.feesSummaryLabel}>Total Fees</Text>
              <Text style={styles.feesSummaryValue}>{fmt(totalFees)}</Text>
            </View>
            <View style={styles.feesSummaryItem}>
              <Text style={styles.feesSummaryLabel}>Total Paid</Text>
              <Text style={styles.feesSummaryValue}>{fmt(totalPaid)}</Text>
            </View>
            <View style={styles.feesSummaryItem}>
              <Text style={styles.feesSummaryLabel}>Pending</Text>
              <Text style={pending > 0 ? styles.feesSummaryValuePending : styles.feesSummaryValue}>
                {fmt(pending)}
              </Text>
            </View>
          </View>
        )}

        {/* ── Thank you ── */}
        <View style={styles.thankYou}>
          <Text style={styles.thankYouText}>Thank you for your payment!</Text>
          <Text style={styles.thankYouSub}>Keep this receipt for your records.</Text>
        </View>

        {/* ── Footer: contact + QR ── */}
        <View style={styles.footer}>
          <View style={styles.coachingInfo}>
            <Text style={styles.coachingLabel}>Connect with us</Text>
            <Text style={styles.coachingLine}>🌐  www.ooklass.in</Text>
            <Text style={styles.coachingLine}>📸  @ooklass.in</Text>
            <Text style={styles.coachingLine}>✉️  ooklass07@gmail.com</Text>
          </View>
          <Image src={QR} style={styles.qr} />
        </View>

        {/* ── Signature ── */}
        <View style={styles.signatureSection}>
          <Text style={styles.signatureNote}>This is a computer-generated receipt.</Text>
          <View>
            <View style={styles.signatureBox}>
              <Text> </Text>
            </View>
            <Text style={styles.signatureLabel}>Authorised Signature</Text>
          </View>
        </View>

      </View>
    </Page>
  </Document>
  );
};

export default InvoicePDF;
